import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { SimpleTreeView, simpleTreeViewClasses } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import InputCheckbox from "common/components/Elements/Form/InputCheckbox/InputCheckbox";
import type { Color } from "common/utilities/colors/colors.types";
import { arrayFlat } from "common/utilities/core/array";
import theme, { sx } from "core/utilities/theme";
import React from "react";
import { SequenceFeatureTypeColorSetting } from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import { areAllFeaturesDefinitionsHidden } from "sequenceAnalyser/utilities/features/features";
import type {
  FeatureDefinition,
  FeatureId,
  SequenceFeaturesType,
  SequenceFeatureWithRef,
} from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import { ColorSources } from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import { ColorButton } from "widgets";

import classes from "./FeaturesTree.style";

/*const featureTypeColorSettingOptions = (): ToggleButtonOption<SequenceFeatureTypeColorSetting>[] => [
  {
    id: "I",
    content: I18n.SequenceAnalyser.Features.FeaturesTree.identical,
    title: I18n.SequenceAnalyser.Features.FeaturesTree.identicalTitle,
  },
  {
    id: "D",
    content: I18n.SequenceAnalyser.Features.FeaturesTree.distinct,
    title: I18n.SequenceAnalyser.Features.FeaturesTree.distinctTitle,
  },
  {
    id: "S",
    content: I18n.SequenceAnalyser.Features.FeaturesTree.shade,
    title: I18n.SequenceAnalyser.Features.FeaturesTree.shadeTitle,
  },
];*/

interface ComponentProps {
  featureDefinitions: FeatureDefinition[];
  featureTypes: SequenceFeaturesType[];
  onFeatureDefinitionsVisibilityChange: (definitions: FeatureDefinition[]) => void;
  onSelectAll: () => void;
  onSelectFeatures: (features: SequenceFeatureWithRef[]) => void;
  onVisibilityChange: (id: FeatureId) => void;
  onColorChange: (typeId: number, label: string, color: Color | undefined) => void;
  onFeatureTypeChange: (
    featureType: SequenceFeaturesType,
    colorSetting: SequenceFeatureTypeColorSetting,
    color: Color | undefined,
  ) => void;
  disableSelect?: boolean;
  onClearFeatureType: (featureType: SequenceFeaturesType) => void;
}

function FeaturesTree({
  featureDefinitions,
  featureTypes,
  onFeatureDefinitionsVisibilityChange,
  onSelectAll,
  onSelectFeatures,
  onVisibilityChange,
  // onColorChange,
  // onFeatureTypeChange,
  disableSelect,
  // onClearFeatureType,
}: ComponentProps) {
  const [expanded, setExpanded] = React.useState<string[]>([
    "all",
    ...featureTypes.filter((ft) => ft.definitions.length <= 10).map((f) => f.type),
  ]);
  const [selected, setSelected] = React.useState<string>("all");
  // const [hover, setHover] = React.useState<string>("");

  // See SEL11-847
  // const [editingFeatureColor, setEditingFeatureColor] = React.useState<FeatureId | string | undefined>();

  const handleToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
    const targetElementTag = ((event as unknown as React.MouseEvent<HTMLElement>).target as HTMLElement).tagName;

    if (["path", "svg"].includes(targetElementTag)) {
      setExpanded(nodeIds);
    }
  };

  const handleSelect = (event: React.SyntheticEvent, nodeIds: string | null) => {
    if (!nodeIds) return;

    const targetElementTag = ((event as unknown as React.MouseEvent<HTMLElement>).target as HTMLElement).tagName;

    // When clicking on the chevron we only want to open/close
    if (!["path", "svg"].includes(targetElementTag)) {
      setSelected(nodeIds);
    }
  };

  return (
    <SimpleTreeView
      sx={sx(
        {
          [simpleTreeViewClasses.root]: classes.root,
        },
        disableSelect ? classes.disableFocus : undefined,
      )}
      slots={{ collapseIcon: ExpandMoreIcon, expandIcon: ChevronRightIcon }}
      expandedItems={expanded}
      selectedItems={selected}
      onExpandedItemsChange={handleToggle}
      onSelectedItemsChange={handleSelect}
      disableSelection={disableSelect}
    >
      <TreeItem
        itemId="all"
        label={
          <Box sx={{ display: "flex" }}>
            <InputCheckbox
              checked={!areAllFeaturesDefinitionsHidden(featureDefinitions)}
              onChange={(e) => {
                onFeatureDefinitionsVisibilityChange(featureDefinitions);
                e.stopPropagation();
              }}
              className={classes.visibilityButton}
              small
            />
            <Box flexGrow={1}>
              <Typography
                variant="body2"
                component="span"
                sx={{
                  ...classes.treeItemText,
                  fontWeight: selected === "all" && !disableSelect ? theme.typography.fontWeightBold : undefined,
                }}
                onClick={disableSelect ? undefined : onSelectAll}
              >
                All
              </Typography>
            </Box>
          </Box>
        }
      >
        {featureTypes.map((featureType) => {
          const featureTypeFeatureDefinitions = featureDefinitions
            .filter((fd) => featureType.type === fd.type)
            .sort((a, b) => a.name.localeCompare(b.name));

          return (
            <TreeItem
              key={featureType.type}
              itemId={featureType.type}
              // onMouseEnter={() => setHover(featureType.type)}
              // onMouseLeave={() => setHover("")}
              label={
                <Box sx={{ display: "flex" }}>
                  <InputCheckbox
                    checked={!areAllFeaturesDefinitionsHidden(featureTypeFeatureDefinitions)}
                    onChange={(e) => {
                      onFeatureDefinitionsVisibilityChange(featureTypeFeatureDefinitions);
                      e.stopPropagation();
                    }}
                    className={classes.visibilityButton}
                    small
                  />
                  {/* See SEL11-847 */}
                  {featureType.colorSetting !== SequenceFeatureTypeColorSetting.D && (
                    <ColorButton isDisabled defaultColor={`rgb(${featureType.color.join(",")})`} size={"tiny"} />
                  )}
                  <Box sx={{ display: "flex", flexGrow: 1 }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{
                          ...classes.treeItemText,
                          fontWeight: selected === featureType.type ? theme.typography.fontWeightBold : undefined,
                        }}
                        onClick={
                          disableSelect
                            ? undefined
                            : () =>
                                onSelectFeatures(
                                  arrayFlat<SequenceFeatureWithRef>(
                                    featureTypeFeatureDefinitions.map((d) => d.features),
                                  ),
                                )
                        }
                      >
                        {featureType.type}
                      </Typography>
                    </Box>

                    {/*{hover === featureType.type && (
                      <Box sx={classes.toggleButtonWrapper}>
                        <ToggleButton
                          variant="dropdown"
                          onChange={(option) => onFeatureTypeChange(featureType, option.id, featureType.color)}
                          options={featureTypeColorSettingOptions()}
                          selectedOption={
                            featureTypeColorSettingOptions().find(
                              (o) => o.id === featureType.colorSetting,
                            ) as ToggleButtonOption<SequenceFeatureTypeColorSetting>
                          }
                        />
                        <Button
                          action="tertiary"
                          onClick={(e) => {
                            e.stopPropagation();
                            onClearFeatureType(featureType);
                          }}
                          small
                          title="Clear all color settings for this feature type and related features"
                        >
                          <Icon element={CancelIcon} />
                        </Button>
                      </Box>
                    )}*/}
                  </Box>
                </Box>
              }
            >
              {featureTypeFeatureDefinitions.map((definition) => {
                const nodeId = definition.uniqueTypeString;

                return (
                  <TreeItem
                    key={nodeId}
                    itemId={nodeId}
                    label={
                      <Box sx={{ display: "flex" }}>
                        <InputCheckbox
                          checked={definition.visible}
                          onChange={(e) => {
                            onVisibilityChange(nodeId);
                            e.stopPropagation();
                          }}
                          className={classes.visibilityButton}
                          small
                        />
                        {/* See SEL11-847 */}
                        <Box
                          sx={{
                            display: "flex",
                            mt: definition.source === ColorSources.forced ? "5px" : undefined,
                          }}
                        >
                          <ColorButton isDisabled defaultColor={`rgb(${definition.color.join(",")})`} size={"tiny"} />
                        </Box>
                        <Box flexGrow={1}>
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{
                              ...classes.treeItemText,
                              fontWeight: selected === nodeId ? theme.typography.fontWeightBold : undefined,
                            }}
                            onClick={disableSelect ? undefined : () => onSelectFeatures(definition.features)}
                          >
                            {definition.name}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                );
              })}
            </TreeItem>
          );
        })}
      </TreeItem>
    </SimpleTreeView>
  );
}

export default FeaturesTree;
